<template>
  <div class="realvideo-container">
    <div class="conference-container__videos">
      <div class="conference-container__videos--remote">
        <div class="video">
          <Video :videoId="aws.videoId" :isPlay="aws.isPlay" :videoStream="aws.remoteStream"> </Video>
        </div>
      </div>
    </div>
    <CommonDialog :dialog="dialog" />
  </div>
</template>

<script>
  import AWS from "aws-sdk";
  import { Auth } from "aws-amplify";
  import { SignalingClient } from "amazon-kinesis-video-streams-webrtc";
  import moment from "moment";
  import Video from "@/components/video/Video";
  import CommonDialog from "@/components/CommonDialog";
  import { commonFunction, communicationFunction } from "@/mixins/utils";
  import * as log from "loglevel";

  /**
   * RealVideo.vue
   * マルチモニター用カメラ映像
   * @author HBA
   */
  export default {
    name: "RealVideo",
    components: {
      Video,
      CommonDialog
    },
    mixins: [commonFunction, communicationFunction],
    props: ["camera"],
    data: function() {
      return {
        setting: undefined,
        selectCamera: undefined,
        waitInterval: undefined,
        aws: {
          region: Auth._config.region,
          channelARN: undefined,
          role: "VIEWER",
          credentials: null,
          endpointsByProtocol: null,
          peerConnection: null,
          client: null,
          useTrickleICE: true,
          remoteView: undefined,
          remoteStream: undefined,
          isPlay: true
        },
        streaming: {
          status: undefined,
          channel: undefined,
          respTimer: []
        },
        dialog: {
          isDialog: false,
          title: "",
          message: ""
        }
      };
    },
    created() {
      this.initialize();
    },
    mounted() {
      if (this.selectCamera !== void 0) {
        // this.startStreaming();
        this.getSignalingChannelName();
      } else {
        this.aws.isPlay = false;
      }
    },
    beforeDestroy() {
      this.allClear();
    },
    methods: {
      initialize() {
        this.allClear();
        this.setting = this.getSetting();
        this.selectCamera = this.camera;
        this.waitInterval = this.setting.realVideo.waitInterval;
        let datetime = moment().valueOf();
        this.aws.videoId = datetime.toString() + "_" + this.$_uid.toString();
      },
      /**
       * シグナリングチャネル名の取得
       */
      async getSignalingChannelName() {
        let promise = this.getSignalingChannel(this.selectCamera.poleId, this.selectCamera.sensorId);
        promise
          .then(res => {
            // APIからの戻り値として"channel"プロパティが含まれているかをチェックする
            if (res !== void 0 && res !== null && res.channel !== void 0 && res.channel !== null && res.channel !== "") {
              this.streaming.channel = res.channel;
              this.streaming.status = "RespCheck";
              // チャネル名が見つかったらビデオ映像ビューワを起動する
              this.startWebrtcViewer();
            } else {
              log.error("Signal channel name is empty.");
            }
          })
          .catch(error => {
            log.error("No signal channel name" + error);
          });
      },
      async startStreaming() {
        let promise = this.streamingStart(this.selectCamera.poleId, this.selectCamera.sensorId);
        promise
          .then(res => {
            this.streaming.channel = res.channel;
            this.streaming.status = "RespCheck";
            this.startStreamingTimer(this.streaming.channel, this.setting.streaming.respChkInterval);
          })
          .catch(error => {
            log.error("startStreaming error " + error);
          });
      },
      startStreamingTimer(channel, interval) {
        this.stopStreamingTimer();
        let self = this;
        this.streaming.respTimer.push(
          setInterval(function() {
            self.timeoutStreamingTimer(channel);
          }, interval)
        );
      },
      stopStreamingTimer() {
        if (this.streaming.respTimer.length > 0) {
          for (const respTimer of this.streaming.respTimer) {
            clearInterval(respTimer);
          }
        }
      },
      async timeoutStreamingTimer(channel) {
        if (this.streaming.status === "RespCheck") {
          this.checkStreaming(channel);
        } else if (this.streaming.status === "Started") {
          this.notifyStreaming(channel);
        } else {
          this.stopStreamingTimer();
        }
      },
      checkStreaming(channel) {
        let promise = this.streamingChkResp(channel);
        promise
          .then(res => {
            if (res.responseStatus === this.streamingChkRespCode.ok) {
              this.stopStreamingTimer();
              this.streaming.status = "Started";
              this.startStreamingTimer(this.streaming.channel, this.setting.streaming.notifyInterval);
            } else if (res.responseStatus === this.streamingChkRespCode.ng) {
              this.stopStreamingTimer();
              this.streaming.status = undefined;
              this.streaming.channel = undefined;
              this.aws.isPlay = false;
            }
          })
          .catch(() => {
            this.stopStreamingTimer();
            this.streaming.status = undefined;
            this.streaming.channel = undefined;
            this.aws.isPlay = false;
          });
      },
      notifyStreaming(channel) {
        let promise = this.streamingActiveNotify(channel);
        promise
          .then(() => {})
          .catch(() => {
            this.allClear();
            this.aws.isPlay = false;
          });
      },
      showErrorDialogue(title, message) {
        this.dialog.title = title;
        this.dialog.message = message;
        this.dialog.isDialog = true;
        this.aws.isPlay = false;
      },
      async startWebrtcViewer() {
        try {
          this.aws.peerConnection = await this.connectSignalingChannel(this.aws.role);
          if (this.aws.peerConnection !== null) {
            this.aws.client = await this.generateSignalingClientViewer(this.aws.peerConnection, this.aws.useTrickleICE);
          }
          if (this.aws.client) {
            this.aws.client.open();
            // ビューワが起動したらストリーミングを開始する
            this.startStreaming();
          } else {
            this.allClear();
            this.aws.isPlay = false;
          }
        } catch (error) {
          this.allClear();
          this.aws.isPlay = false;
          log.error("startWebrtcViewer error " + error);
        }
      },
      async endWebrtcViewer() {
        try {
          if (this.aws.client != null) {
            this.aws.client.close();
            this.aws.client = null;
          }
          if (this.aws.peerConnection) {
            this.aws.peerConnection.close();
            this.aws.peerConnection = null;
          }
          if (this.aws.remoteStream) {
            this.aws.remoteStream.getTracks().forEach(track => track.stop());
            this.aws.remoteStream = null;
          }
          if (this.aws.remoteView) {
            this.aws.remoteView.srcObject = null;
          }
          this.aws.isPlay = true;
        } catch (error) {
          log.error("endWebrtcViewer error " + error);
        }
      },
      async connectSignalingChannel(role) {
        this.aws.credentials = await this.getCredential();

        if (!this.aws.credentials) return null;

        const kvClient = new AWS.KinesisVideo({
          region: this.aws.region,
          credentials: this.aws.credentials
        });

        const describeSignalingChannelResponse = await kvClient
          .describeSignalingChannel({
            ChannelName: this.streaming.channel
          })
          .promise();

        this.aws.channelArn = describeSignalingChannelResponse.ChannelInfo.ChannelARN;

        this.aws.endpointsByProtocol = await this.fetchEndpoints({
          kvClient,
          channelARN: this.aws.channelArn,
          role: role
        });

        const kvsChannelsClient = new AWS.KinesisVideoSignalingChannels({
          region: this.aws.region,
          credentials: this.aws.credentials,
          endpoint: this.aws.endpointsByProtocol.HTTPS
        });

        const iceServers = await this.fetchTURNServers({
          kvsChannelsClient,
          channelARN: this.aws.channelArn,
          region: this.aws.region
        });

        return new RTCPeerConnection({ iceServers });
      },
      async getCredential() {
        try {
          await Auth.currentAuthenticatedUser();
          const credentials = await Auth.currentCredentials();
          return await Auth.essentialCredentials(credentials);
        } catch (err) {
          log.error("getCredential error " + err);
          return null;
        }
      },
      async fetchEndpoints({ kvClient, channelARN, role }) {
        const getSignalingChannelEndpointResponse = await kvClient
          .getSignalingChannelEndpoint({
            ChannelARN: channelARN,
            SingleMasterChannelEndpointConfiguration: {
              Protocols: ["WSS", "HTTPS"],
              Role: role
            }
          })
          .promise();

        const endpointsByProtocol = getSignalingChannelEndpointResponse.ResourceEndpointList.reduce((endpoints, endpoint) => {
          endpoints[endpoint.Protocol] = endpoint.ResourceEndpoint;
          return endpoints;
        }, {});

        return endpointsByProtocol;
      },
      async fetchTURNServers({ kvsChannelsClient, channelARN, region }) {
        const getIceServerConfigResponse = await kvsChannelsClient
          .getIceServerConfig({
            ChannelARN: channelARN
          })
          .promise();

        const iceServers = [{ urls: `stun:stun.kinesisvideo.${region}.amazonaws.com:443` }];
        getIceServerConfigResponse.IceServerList.forEach(iceServer =>
          iceServers.push({
            urls: iceServer.Uris,
            username: iceServer.Username,
            credential: iceServer.Password
          })
        );
        return iceServers;
      },
      async generateSignalingClientViewer(peerConnection, useTrickleICE) {
        if (!this.aws.credentials) return null;

        const signalingClient = new SignalingClient({
          channelARN: this.aws.channelArn,
          channelEndpoint: this.aws.endpointsByProtocol.WSS,
          clientId: `c${Date.now()}`, // should be random
          role: "VIEWER",
          region: this.aws.region,
          credentials: this.aws.credentials
        });

        signalingClient.on("open", async () => {
          log.info("[VIEWER] Connected to signaling service");
          log.info("[VIEWER] Creating SDP offer");
          await peerConnection.setLocalDescription(
            await peerConnection.createOffer({
              offerToReceiveAudio: true,
              offerToReceiveVideo: true
            })
          );
          log.info("[VIEWER] Sending SDP offer");
          signalingClient.sendSdpOffer(peerConnection.localDescription);
        });
        signalingClient.on("sdpAnswer", async answer => {
          log.info("[VIEWER] Received SDP answer");
          await peerConnection.setRemoteDescription(answer);
        });
        signalingClient.on("iceCandidate", candidate => {
          log.info("[VIEWER] Received ICE candidate");
          peerConnection.addIceCandidate(candidate);
        });
        signalingClient.on("close", () => {
          log.info("[VIEWER] Disconnected from signaling channel");
        });

        signalingClient.on("error", error => {
          log.error("[VIEWER] Signaling client error: " + error);
        });

        // Send any ICE candidates to the other peer
        peerConnection.addEventListener("icecandidate", ({ candidate }) => {
          if (candidate) {
            log.info("[VIEWER] Generated ICE candidate");

            // When trickle ICE is enabled, send the ICE candidates as they are generated.
            if (useTrickleICE) {
              log.info("[VIEWER] Sending ICE candidate");
              signalingClient.sendIceCandidate(candidate);
            }
          } else {
            log.info("[VIEWER] All ICE candidates have been generated");

            // When trickle ICE is disabled, send the offer now that all the ICE candidates have ben generated.
            if (!useTrickleICE) {
              log.info("[VIEWER] Sending SDP offer");
              signalingClient.sendSdpOffer(peerConnection.localDescription);
            }
          }
        });

        // As remote tracks are received, add them to the remote view
        peerConnection.addEventListener("track", event => {
          log.info("[VIEWER] Received remote track");
          this.aws.remoteView = document.getElementById(this.aws.videoId);
          if (!this.aws.remoteView.srcObject && event.streams) {
            this.aws.remoteStream = event.streams[0];
            this.aws.remoteView.srcObject = this.aws.remoteStream;
          }
        });

        return signalingClient;
      },
      allClear() {
        this.stopStreamingTimer();
        this.streaming.status = undefined;
        this.streaming.channel = undefined;
        this.endWebrtcViewer();
      },
      changeCamera(selectCamera) {
        this.selectCamera = selectCamera;
        this.allClear();
        setTimeout(
          function() {
            this.startStreaming();
          }.bind(this),
          this.waitInterval
        );
      }
    },
    watch: {
      camera(selectCamera) {
        if (selectCamera == undefined) {
          this.allClear();
          this.aws.isPlay = false;
        } else {
          this.changeCamera(selectCamera);
        }
      }
    }
  };
</script>

<style lang="scss" scoped>
  @import "@/styles/variables";

  .realvideo-container {
    background-color: black;
    height: 42vh;
    width: 100%;
    &__header {
      display: flex;
      justify-content: space-between;
      width: 100%;
      height: 50px;
      background-color: black;
      .md-button {
        transform: translateY(7px);
      }
      .md-icon.md-theme-default.md-icon-font {
        color: white;
      }
    }
    &__videos {
      position: relative;
      border: solid 1px #ffffff78;
      &--remote {
        position: relative;
      }
    }
    .video {
      width: 100%;
      height: 42vh;
    }
    h3 {
      padding-left: 1rem;
      color: white;
    }
  }
</style>
